import {
  DynamicIconType,
  FieldTypeLink,
  TileBackground,
} from '@allesauto/utils/consts';
import {
  getUrlFromStoryblokLink,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import { useWhiteLabelThemes } from '@allesauto/utils/hooks.ts';
import { ArrowRight32 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import DynamicIcon from '../dynamicIcon';
import NextLink from '../nextLink';
import { useWhiteLabel } from '../whiteLabel';

export interface TileProps {
  background: TileBackground;
  headline: string;
  text: string;
  link: FieldTypeLink | string;
  order?: number;
  icon?: DynamicIconType;
}

export default function Tile({
  background,
  headline,
  text,
  link,
  order,
  icon = 'search',
}: TileProps) {
  const whiteLabel = useWhiteLabel()!;
  const [tileOrder, setTileOrder] = useState(0);
  let bgColor;
  let accentColor;
  let textColor;
  let borderColor;

  const bg = background in TileBackground ? background : TileBackground.PURPLE;

  const {
    accent,
    border,
    background: bgColour,
  } = useWhiteLabelThemes(whiteLabel, bg);

  switch (bg) {
    case TileBackground.PURPLE:
      bgColor = whiteLabel ? bgColour : 'bg-purple80 monochrome:bg-grey80';
      accentColor = whiteLabel ? accent : 'text-green60 monochrome:text-white';
      textColor = '!text-white';
      borderColor = whiteLabel ? border : 'bg-white monochrome:bg-grey40';
      break;
    case TileBackground.GREEN:
      bgColor = 'bg-green60 monochrome:bg-grey20';
      accentColor = 'text-purple80 monochrome:text-grey90';
      textColor = 'text-grey100';
      borderColor = whiteLabel ? border : 'bg-purple80 monochrome:bg-grey90';
      break;
    case TileBackground.WHITE:
      bgColor = 'bg-white';
      accentColor = 'text-purple80 monochrome:text-grey90';
      textColor = 'text-grey100 monochrome:text-grey90';
      borderColor = 'bg-green60 monochrome:bg-grey60';
      break;
    default:
    case TileBackground.GREY:
      bgColor = whiteLabel ? bgColour : 'bg-grey80';
      accentColor = whiteLabel ? accent : 'text-green60 monochrome:text-white';
      textColor = '!text-white';
      borderColor = whiteLabel ? border : 'bg-green60 monochrome:bg-grey40';
      break;
  }

  const className = {
    tile: clsx(
      'flex flex-col group no-underline pb-[3.375rem] pt-40px px-30px relative md:px-40px lg:pb-[4.5rem] lg:pt-[3.125rem] lg:px-60px xl:px-80px hover:cursor-pointer',
      bgColor
    ),
    headline: clsx(
      'font-headline font-weight-bold mb-20px text-headline4 lg:h-[40px]',
      accentColor
    ),
    text: clsx(textColor, 'overflow-hidden lg:h-60px'),
    icon: clsx(
      'absolute bottom-20px duration-300ms ease-[cubic-bezier(0,0,0.58,1)] h-[1.5rem] opacity-0 right-30px self-end transition-all w-[1.5rem] lg:bottom-30px lg:group-focus:opacity-0 lg:group-hover:opacity-0 lg:h-[2rem] lg:opacity-100 lg:w-[2rem]',
      accentColor
    ),
    border: clsx(
      'absolute bottom-0 duration-300 ease-linear h-0 left-0 mt-0 overflow-hidden transition-all w-full z-10 lg:group-focus:h-[7px] lg:group-focus:mt-[-7px] lg:group-hover:h-[7px] lg:group-hover:mt-[-7px]',
      borderColor
    ),
    arrow: clsx(
      'absolute bottom-20px duration-300 ease-[cubic-bezier(0,0,0.58,1)] h-[1.5rem] right-30px self-end transition-all w-[1.5rem] lg:bottom-30px lg:group-focus:opacity-100 lg:group-hover:opacity-100 lg:h-[2rem] lg:opacity-0 lg:w-[2rem]',
      accentColor
    ),
    iconSvg: 'w-full h-full',
  };

  const style = {
    order: tileOrder,
  };

  useEffect(() => {
    order && setTileOrder(order);
  }, [order]);

  const url = typeof link === 'string' ? link : getUrlFromStoryblokLink(link);
  let tileLink = (url && parseSlashToURL(url)) || '';
  if (tileLink.endsWith('/') && typeof link === 'string') {
    tileLink = tileLink.substring(0, tileLink.length - 1);
  }

  return (
    <NextLink
      href={tileLink}
      target={typeof link === 'string' ? '_self' : link?.target ?? '_self'}
      passHref
      className={className.tile}
      style={style}
    >
      <div className={className.headline}>{headline}</div>
      <div className={className.text}>{text}</div>
      <div className={className.icon}>
        <DynamicIcon icon={icon} />
      </div>
      <div className={className.arrow}>
        <ArrowRight32 className={className.iconSvg} />
      </div>
      <div className={className.border}></div>
    </NextLink>
  );
}
